// The API refuses a request in two different shapes, and neither of them rejects.
// AxiosClient resolves failures rather than throwing, and the controllers answer a domain
// error with 200 plus success:false. Both have to be read deliberately or a refusal reads
// as success - which is exactly how a rejected registration came to be followed by creating
// the staff row anyway, leaving two accounts sharing one user name.
//
// Returns null when the result is not a refusal. Otherwise { message, field, fields }:
// `fields` lists every field the server named, so more than one input can be marked at once,
// and `message` is the first of them (or a general sentence when no field was named).

// The server names fields from two places - the registration endpoint sends "userName", the
// staff service sends the C# property name "UserName" - so compare case-insensitively.
const KNOWN_FIELDS = ['userName', 'email'];

function normaliseField(name) {
  if (!name) {
    return null;
  }
  const match = KNOWN_FIELDS.find(
    (f) => f.toLowerCase() === String(name).trim().toLowerCase(),
  );
  return match || null;
}

// The per-field errors normally arrive already parsed: the controllers deserialise the
// UserException payload into `errorMessages`, leaving `errorMessage` null.
function readFieldErrors(list) {
  if (!Array.isArray(list)) {
    return [];
  }
  return list
    .filter((e) => e && e.message)
    .map((e) => ({ field: normaliseField(e.name), message: e.message }));
}

// Controllers that do not do that deserialisation leave the same list serialised in
// ErrorMessage instead. Anything that is not that list is an ordinary sentence.
function parseFieldErrors(errorMessage) {
  if (typeof errorMessage !== 'string' || errorMessage.trim()[0] !== '[') {
    return [];
  }
  let parsed;
  try {
    parsed = JSON.parse(errorMessage);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed
    .filter((e) => e && e.Message)
    .map((e) => ({ field: normaliseField(e.Name), message: e.Message }));
}

function refusal(message, fields) {
  const named = (fields || []).filter((f) => f.message);
  return {
    message: named.length ? named[0].message : message,
    field: named.length ? named[0].field : null,
    fields: named,
  };
}

export function apiRefusal(result) {
  if (!result) {
    return refusal('No response from the server. Please try again.', []);
  }

  // A transport or status failure, parked on .error by AxiosClient's interceptor.
  if (result.error) {
    const body =
      (result.error.response && result.error.response.data) || null;
    if (body && body.message) {
      return refusal(body.message, [
        { field: normaliseField(body.field), message: body.message },
      ]);
    }
    return refusal(
      (result.error && result.error.message) ||
        'The server rejected the request.',
      [],
    );
  }

  const data = result.data;
  if (data && data.success === false) {
    const fields = readFieldErrors(data.errorMessages);
    return refusal(
      data.errorMessage ||
        data.message ||
        'The server rejected the request.',
      fields.length ? fields : parseFieldErrors(data.errorMessage),
    );
  }

  return null;
}
